import React, { useEffect, useState } from 'react';
import { Button } from 'components/common';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  FaPaperPlane, FaTimes, FaEnvelope, FaUser, FaUsers,
  FaBuilding, FaSpinner, FaCheckSquare, FaEdit,
} from 'react-icons/fa';
import { MdSubject } from 'react-icons/md';
import TinymceEditor from 'components/common/TinymceEditor';
import RecipientSelect from 'components/common/select/RecipientSelect';
import { getSalesEmailConfig } from 'redux/crm/releaseForecastSlice';
import { recipientGreeting } from '../components/helpers';

// ── Month label helper ────────────────────────────────────────────────────────
// "May-2026" → "JUN-JUL-AUG 2026"
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const getThreeMonthLabel = (period) => {
  if (!period) return '';
  const [mon, yr] = period.split('-');
  const idx = MONTHS.findIndex(m => m.toLowerCase() === mon.toLowerCase());
  if (idx === -1 || !yr) return '';
  const year = parseInt(yr, 10);
  const labels = [1, 2, 3].map(offset => {
    const mi = (idx + offset) % 12;
    return MONTHS[mi].toUpperCase();
  });
  const lastYear = idx + 3 > 11 ? year + 1 : year;
  return `${labels.join('-')} ${lastYear}`;
};

// ── Build default email body (HTML) ──────────────────────────────────────────
const buildDefaultBody = ({ toName, subject, fromName, url }) =>
`<p>Dear <strong>${toName}</strong>,</p>
<p>The Sales Release Forecast for <strong>${subject}</strong> has been released and is ready for review.</p>
<p>Please click the link below to access the complete forecast: <a href="${url}" target="_blank">View Forecast</a></p>
<p>Best Regards,<br>${fromName}</p>`;

// ── react-select styles ───────────────────────────────────────────────────────
// ── Custom option ─────────────────────────────────────────────────────────────
const EmailSelectField = ({ label, icon: Icon, value, onChange }) => (
  <div className="tw-flex tw-items-start tw-gap-3 tw-px-4 tw-py-2 tw-border-b tw-border-slate-100">
    <div className="tw-flex tw-items-center tw-gap-1.5 tw-w-16 tw-shrink-0 tw-pt-1.5">
      <Icon size={11} className="tw-text-slate-400" />
      <span className="tw-text-[10px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-widest">{label}</span>
    </div>
    <div className="tw-flex-1">
      <RecipientSelect value={value} onChange={onChange} placeholder="Search employee or type email…" />
    </div>
  </div>
);
EmailSelectField.propTypes = {
  label:    PropTypes.string.isRequired,
  icon:     PropTypes.elementType.isRequired,
  value:    PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

// ── ReleaseConfirmDialog ──────────────────────────────────────────────────────
const ReleaseConfirmDialog = ({ forecastPeriod, selectedIds, selectedCount, onConfirm, onCancel }) => {
  const dispatch = useDispatch();

  const [toEmails,  setToEmails]  = useState([]);
  const [ccEmails,  setCcEmails]  = useState([]);
  const [fromEmail, setFromEmail] = useState('');
  const [fromName,  setFromName]  = useState('');
  const [replyTo,   setReplyTo]   = useState('');
  const [salesUnit, setSalesUnit] = useState('');
  const [subject,   setSubject]   = useState('');
  const [body,      setBody]      = useState('');
  const [loading,   setLoading]   = useState(true);

  const monthLabel = getThreeMonthLabel(forecastPeriod);

  // Recompute subject whenever salesUnit or period changes
  useEffect(() => {
    const unit = salesUnit || 'PEBS';
    const suffix = monthLabel ? ` ${monthLabel}` : '';
    setSubject(`${unit} - SALES RELEASE FORECAST${suffix}`);
  }, [salesUnit, monthLabel]);

  // Recompute default body whenever subject, toEmails, or fromName changes
  useEffect(() => {
    const toName  = recipientGreeting(toEmails, 'CSD');
    const url     = `${window.location.origin}/crm/release-forecast/csd${forecastPeriod ? `?period=${forecastPeriod}` : ''}`;
    setBody(buildDefaultBody({ toName, subject, fromName, url }));
  }, [subject, toEmails, fromName, forecastPeriod]);

  useEffect(() => {
    dispatch(getSalesEmailConfig())
      .unwrap()
      .then(data => {
        setToEmails(data.to_emails  || []);
        setCcEmails(data.cc_emails  || []);
        setFromEmail(data.from_email || '');
        setFromName(data.from_name  || '');
        setReplyTo(data.reply_to   || '');
        setSalesUnit(data.sales_unit || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dispatch]);

  const handleConfirm = () => {
    if (toEmails.length === 0) return;
    onConfirm({ toEmails, ccEmails, saleIds: selectedIds, subject, body });
  };

  const hasSelection = selectedIds && selectedIds.length > 0;

  return (
    <div
      className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-p-4 tw-bg-black/50"
      style={{ zIndex: 999999 }}
    >
      <div className="tw-bg-white tw-rounded-lg tw-shadow-2xl tw-w-full tw-max-w-2xl tw-overflow-hidden tw-flex tw-flex-col tw-max-h-[90vh]">

        {/* ── Header ── */}
        <div className="tw-flex tw-items-center tw-gap-3 tw-px-5 tw-py-4 tw-bg-gradient-to-r tw-from-blue-600 tw-to-blue-700 tw-shrink-0">
          <span className="tw-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-rounded-xl tw-bg-white/20">
            <FaEnvelope size={16} className="tw-text-white" />
          </span>
          <div>
            <p className="tw-text-sm tw-font-bold tw-text-white tw-mb-0">Release Sales Forecast to CSD</p>
            <p className="tw-text-xs tw-text-blue-200 tw-mb-0">Compose and confirm the release email</p>
          </div>
          <button
            type="button" onClick={onCancel}
            className="tw-ml-auto tw-border-0 tw-bg-white/10 hover:tw-bg-white/20 tw-text-white tw-rounded tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-transition-colors"
          >
            <FaTimes size={12} />
          </button>
        </div>

        {/* ── Selection badge ── */}
        {hasSelection && (
          <div className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-blue-50 tw-border-b tw-border-blue-100 tw-shrink-0">
            <FaCheckSquare size={12} className="tw-text-blue-500 tw-shrink-0" />
            <p className="tw-text-xs tw-text-blue-700 tw-mb-0">
              Releasing <strong>{selectedIds.length}</strong> selected record{selectedIds.length !== 1 ? 's' : ''} only
              {selectedCount && selectedCount > selectedIds.length && (
                <span className="tw-text-blue-400"> (out of {selectedCount} total)</span>
              )}
            </p>
          </div>
        )}

        {/* ── Scrollable body ── */}
        <div className="tw-flex-1 tw-overflow-y-auto">
          {loading ? (
            <div className="tw-flex tw-items-center tw-justify-center tw-py-16 tw-gap-2 tw-text-slate-400">
              <FaSpinner size={16} className="tw-animate-spin" />
              <span className="tw-text-sm">Loading email settings…</span>
            </div>
          ) : (
            <div className="tw-px-4 tw-pt-4 tw-pb-2 tw-flex tw-flex-col tw-gap-3">

              {/* ── Email header card ── */}
              <div className="tw-border tw-border-slate-200 tw-rounded tw-overflow-hidden tw-shadow-sm">

                {/* From */}
                <div className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-2.5 tw-border-b tw-border-slate-100 tw-bg-slate-50/60">
                  <div className="tw-flex tw-items-center tw-gap-1.5 tw-w-16 tw-shrink-0">
                    <FaUser size={11} className="tw-text-slate-400" />
                    <span className="tw-text-[10px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-widest">From</span>
                  </div>
                  {/* Mail is sent from the portal address under the user's name;
                      replies go back to the user (ApplicationMailer#sender_as). */}
                  <div className="tw-flex-1 tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                    <span className="tw-text-xs tw-font-semibold tw-text-slate-700">{fromName}</span>
                    {fromEmail && <span className="tw-text-xs tw-text-slate-400">&lt;{fromEmail}&gt;</span>}
                    {replyTo && <span className="tw-text-xs tw-text-slate-400">· replies to {replyTo}</span>}
                    {salesUnit && (
                      <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-1.5 tw-py-0.5 tw-rounded-sm tw-bg-primary-50 tw-text-primary-600 tw-text-[10px] tw-font-semibold tw-border tw-border-primary-100">
                        <FaBuilding size={9} /> {salesUnit}
                      </span>
                    )}
                  </div>
                </div>

                {/* To */}
                <EmailSelectField label="To" icon={FaUsers} value={toEmails} onChange={setToEmails} />

                {/* CC */}
                <EmailSelectField label="CC" icon={FaUsers} value={ccEmails} onChange={setCcEmails} />

                {/* Subject — editable */}
                <div className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-2">
                  <div className="tw-flex tw-items-center tw-gap-1.5 tw-w-16 tw-shrink-0">
                    <MdSubject size={13} className="tw-text-slate-400" />
                    <span className="tw-text-[10px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-widest">Subject</span>
                  </div>
                  <input
                    type="text"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    className="tw-flex-1 tw-text-xs tw-font-medium tw-text-slate-700 tw-border-0 tw-outline-none tw-bg-transparent tw-py-0.5"
                  />
                </div>
              </div>

              {/* ── Body — rich editor ── */}
              <div className="tw-flex tw-flex-col tw-gap-1.5">
                <div className="tw-flex tw-items-center tw-gap-1.5">
                  <FaEdit size={11} className="tw-text-slate-400" />
                  <span className="tw-text-[10px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-widest">Email content</span>
                  <span className="tw-text-[10px] tw-text-slate-300 tw-ml-1">(editable)</span>
                </div>
                <div className="tw-border tw-border-slate-200 tw-rounded tw-overflow-hidden">
                  <TinymceEditor
                    value={body}
                    handleChange={setBody}
                    height="240px"
                    initOverride={{
                      menubar: false,
                      plugins: 'lists link',
                      toolbar: 'bold italic underline | bullist numlist | link | removeformat',
                      placeholder: 'Compose your email content…',
                    }}
                  />
                </div>
              </div>

            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="tw-flex tw-gap-2 tw-justify-end tw-px-4 tw-py-3 tw-border-t tw-border-slate-100 tw-shrink-0">
          <Button variant="secondary" onClick={onCancel}>
            <FaTimes className="tw-mr-1.5 tw-inline" size={11} /> Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleConfirm}
            disabled={loading || toEmails.length === 0}
          >
            <FaPaperPlane size={11} /> Send &amp; Release
          </Button>
        </div>

      </div>
    </div>
  );
};

ReleaseConfirmDialog.propTypes = {
  forecastPeriod: PropTypes.string,
  selectedIds:    PropTypes.array,
  selectedCount:  PropTypes.number,
  onConfirm:      PropTypes.func.isRequired,
  onCancel:       PropTypes.func.isRequired,
};

export default ReleaseConfirmDialog;
